import { useCallback, useEffect, useState } from 'react';

/** What arrived, by the ids of the bell's items: a count that moves once per arrival. */
export interface IBellArrivals {
  count: number;
  arrived: readonly { id: string }[];
}

type Arrived = { id: string };

/** How long what just arrived stays under the bell. */
export const PEEK_MS = 2_000;

/** `arrived` on top of what was showing, each item once, at its newest place. */
function stack(arrived: readonly Arrived[], showing: readonly Arrived[]): Arrived[] {
  const fresh = new Set(arrived.map((n) => n.id));
  return [...arrived, ...showing.filter((n) => !fresh.has(n.id))];
}

/**
 * What to show under the bell for a moment: what arrived, newest on top,
 * for `PEEK_MS` after the last arrival — a further arrival while it shows
 * joins above the rest and starts the time again — and nothing once that
 * passes or the peek is dismissed. The bell dismisses it when its menu
 * opens, which shows everything anyway. While the pointer rests on the peek
 * the time stands still, and it starts again in full when the pointer
 * leaves.
 */
export function useArrivalPeek(arrivals: IBellArrivals): {
  peek: readonly Arrived[];
  dismiss: () => void;
  onHoverChange: (hovering: boolean) => void;
} {
  // The arrival count the peek was last dismissed on, by time or by hand.
  const [dismissedAt, setDismissedAt] = useState(0);
  const [hovering, setHovering] = useState(false);
  const showing = arrivals.count > dismissedAt;

  // What has arrived since the peek was last dismissed, kept across renders
  // and brought up to date as each arrival is first seen.
  const [seen, setSeen] = useState<{ count: number; stacked: Arrived[] }>({ count: 0, stacked: [] });
  if (seen.count !== arrivals.count) {
    // Stack onto what was showing before this arrival; after a gone peek, start afresh.
    const wasShowing = seen.count > dismissedAt;
    setSeen({ count: arrivals.count, stacked: wasShowing ? stack(arrivals.arrived, seen.stacked) : [...arrivals.arrived] });
  }

  useEffect(() => {
    if (!showing || hovering) return;
    const timer = setTimeout(() => setDismissedAt(arrivals.count), PEEK_MS);
    return () => clearTimeout(timer);
  }, [showing, hovering, arrivals.count]);
  const dismiss = useCallback(() => setDismissedAt(arrivals.count), [arrivals.count]);

  return { peek: showing ? seen.stacked : [], dismiss, onHoverChange: setHovering };
}
